// WfhAdminPage — admin inbox for staff WFH requests.
// 1:1 port of the WP plugin's admin/views/wfh.php screen:
//   - filter by status (default: pending)
//   - approve / reject  → PATCH /api/attendance/wfh/:id/decision
//   - delete request    → DELETE /api/attendance/wfh/:id
//   - on approve, the backend auto-creates attendance rows for the WFH dates
//     (per dfhr_handle_update_wfh_status — see ajax-handlers.php L800).

import React, { useEffect, useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, REQUEST_TONES } from "../../Components/ui";
import OverrideModal from "../../Components/OverrideModal";

// Shared approval-lifecycle tones (see Components/ui/statusTones.js). Was a
// private copy of four hardcoded light-theme pairs, duplicated across five
// pages and unreadable-looking in dark mode.
const STATUS_STYLE = REQUEST_TONES;

const inputStyle = { border: "1px solid var(--border-default)", borderRadius: 8, padding: "6px 10px", fontSize: 13, background: "var(--bg-surface)", color: "var(--text-primary)" };
const btnPrimary = { background: "var(--fill-success)", color: "white", padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const btnReject  = { background: "var(--fill-danger)", color: "white", padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const btnGhost   = { background: "transparent", color: "var(--text-secondary)", padding: "5px 10px", border: "1px solid var(--border-default)", borderRadius: 6, fontSize: 12, cursor: "pointer" };

function fmtDate(d) { try { return new Date(d).toISOString().slice(0, 10); } catch { return "—"; } }

export default function WfhAdminPage() {
  const toast = useToast();
  useShellHeader({ title: "WFH Requests", breadcrumb: "Admin · WFH inbox" }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [override, setOverride] = useState(null); // { kind: "cancel"|"edit", row }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/attendance/wfh${status ? `?status=${status}` : ""}`);
      setRows(Array.isArray(r) ? r : (r.rows || r.wfh || []));
    } catch (e) {
      toast({ title: e.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }, [status, toast]);

  useEffect(() => { load(); }, [load]);

  async function decide(id, decision) {
    const promptMsg = decision === "approved" ? "Approval note (optional):" : "Rejection reason (optional):";
    const note = window.prompt(promptMsg) || "";
    try {
      await api(`/attendance/wfh/${id}/decision`, {
        method: "PATCH",
        body: JSON.stringify({ decision, adminNote: note }),
      });
      toast({ title: decision === "approved" ? "WFH approved." : "WFH rejected.", status: "success" });
      load();
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
    }
  }

  async function submitOverride({ reason, values }) {
    const { kind, row } = override;
    try {
      if (kind === "cancel") {
        const r = await api(`/attendance/wfh/${row._id}/cancel`, {
          method: "POST",
          body: JSON.stringify({ reason }),
        });
        const fixed = r?.reconcile?.rederived || 0;
        toast({
          title: "WFH cancelled.",
          description: fixed ? `${fixed} attendance day(s) re-derived from WFH.` : undefined,
          status: "success",
        });
      } else {
        await api(`/attendance/wfh/${row._id}/admin`, {
          method: "PATCH",
          body: JSON.stringify({
            reason,
            fromDate: values.fromDate,
            toDate: values.toDate,
            requestReason: values.requestReason,
            adminNote: values.adminNote,
          }),
        });
        toast({ title: "WFH updated.", status: "success" });
      }
      load();
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
      throw e; // keep the modal open
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>WFH Requests</h2>
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
            Approve, reject, edit, or cancel staff WFH requests. Cancelling an approved WFH restores the office-WiFi check-in requirement for those dates and re-derives any check-ins already logged.
          </div>
        </div>
        <select style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <Card padding="none">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead><tr>
              {["Staff", "From", "To", "Reason", "Status", "Admin Note", "Submitted", "Actions"].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ ...tdBase, textAlign: "center", color: "var(--text-tertiary)" }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} style={{ ...tdBase, textAlign: "center", color: "var(--text-tertiary)", padding: "24px 12px" }}>No WFH requests {status ? `with status "${status}"` : ""}.</td></tr>
              ) : rows.map((r) => {
                const s = r.staff || {};
                const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.username || s.email || "—";
                const style = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                return (
                  <tr key={r._id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td style={tdBase}>
                      <div style={{ fontWeight: 500 }}>{name}</div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{s.email || ""}</div>
                    </td>
                    <td className="tabular" style={tdBase}>{fmtDate(r.fromDate)}</td>
                    <td className="tabular" style={tdBase}>{fmtDate(r.toDate)}</td>
                    <td style={{ ...tdBase, maxWidth: 320, color: "var(--text-secondary)" }}>{r.reason || "—"}</td>
                    <td style={tdBase}>
                      <span style={{ background: style.bg, color: style.co, padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{r.status}</span>
                    </td>
                    <td style={{ ...tdBase, color: "var(--text-tertiary)", fontSize: 12 }}>{r.adminNote || "—"}</td>
                    <td className="tabular" style={tdBase}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : "—"}</td>
                    <td style={tdBase}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {r.status === "pending" && (
                          <>
                            <button style={btnPrimary} onClick={() => decide(r._id, "approved")}>Approve</button>
                            <button style={btnReject} onClick={() => decide(r._id, "rejected")}>Reject</button>
                          </>
                        )}
                        {(r.status === "pending" || r.status === "approved") && (
                          <>
                            <button style={btnGhost} onClick={() => setOverride({ kind: "edit", row: r })}>Edit</button>
                            <button style={btnReject} onClick={() => setOverride({ kind: "cancel", row: r })}>Cancel</button>
                          </>
                        )}
                        {(r.status === "rejected" || r.status === "cancelled") && (
                          <span style={{ color: "var(--text-tertiary)" }}>—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {override && (
        <OverrideModal
          isOpen={!!override}
          onClose={() => setOverride(null)}
          title={override.kind === "cancel" ? "Cancel WFH request" : "Edit WFH request"}
          description={ovDescription(override.row)}
          confirmLabel={override.kind === "cancel" ? "Cancel WFH" : "Save changes"}
          confirmColorScheme={override.kind === "cancel" ? "red" : "blue"}
          fields={override.kind === "edit" ? [
            { name: "fromDate", label: "From", type: "date", value: fmtDate(override.row.fromDate) },
            { name: "toDate", label: "To", type: "date", value: fmtDate(override.row.toDate) },
            { name: "requestReason", label: "WFH reason", type: "textarea", value: override.row.reason || "" },
            { name: "adminNote", label: "Admin note", type: "text", value: override.row.adminNote || "" },
          ] : []}
          onSubmit={submitOverride}
        />
      )}
    </div>
  );
}

function ovDescription(row) {
  const s = row.staff || {};
  const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.username || s.email || "—";
  const range = fmtDate(row.fromDate) === fmtDate(row.toDate)
    ? fmtDate(row.fromDate) : `${fmtDate(row.fromDate)} → ${fmtDate(row.toDate)}`;
  return `${name} · ${range} · currently ${row.status}`;
}

const thStyle = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "10px 14px", color: "var(--text-primary)", verticalAlign: "top" };
